import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { AskQuestionModal } from "@/components/academy/AskQuestionModal";
import { getGlossaryTerm } from "@/lib/academy.functions";

export const Route = createFileRoute("/academy/glossary/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug.replace(/-/g, " ")} — Currency Thoughts Glossary` },
    ],
  }),
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData({
      queryKey: ["academy", "glossary", params.slug],
      queryFn: () => getGlossaryTerm({ data: { slug: params.slug } }),
    });
    if (!data) throw notFound();
  },
  component: GlossaryTermPage,
  notFoundComponent: () => (
    <div className="bg-cream-50">
      <Navbar />
      <div className="mx-auto max-w-2xl px-5 py-32 text-center">
        <h1 className="font-display text-4xl text-navy-900">Term not found</h1>
        <Link to="/academy/glossary" className="mt-6 inline-block text-bronze-600">
          Back to glossary
        </Link>
      </div>
      <Footer />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="bg-cream-50">
      <Navbar />
      <div className="mx-auto max-w-2xl px-5 py-32 text-center">
        <p className="font-sans text-charcoal-500">{error.message}</p>
      </div>
      <Footer />
    </div>
  ),
});

const CATEGORY_LABEL: Record<string, string> = {
  insurance: "Insurance",
  retirement: "Retirement",
  estate: "Estate",
  tax: "Tax",
  investment: "Investment",
  business: "Business",
  general: "General",
};

function GlossaryTermPage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery({
    queryKey: ["academy", "glossary", slug],
    queryFn: () => getGlossaryTerm({ data: { slug } }),
  });
  const [askOpen, setAskOpen] = useState(false);
  if (!data) return null;
  const { term, related, lessons } = data;

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        <section className="bg-navy-900 text-cream-50">
          <div className="mx-auto max-w-4xl px-5 py-16 md:px-10 md:py-20">
            <Link
              to="/academy/glossary"
              className="inline-flex items-center gap-2 font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-bronze-400 hover:text-cream-50"
            >
              <ArrowLeft className="h-3 w-3" /> Back to glossary
            </Link>
            <div className="mt-6 flex items-center gap-3">
              <span className="bg-bronze-400/20 px-3 py-1 font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-400">
                {CATEGORY_LABEL[term.category] ?? term.category}
              </span>
            </div>
            <h1 className="mt-4 font-display text-[44px] leading-[1.05] md:text-[64px]">
              {term.term}
            </h1>
            {term.short_definition && (
              <p className="mt-5 max-w-2xl font-display text-[20px] italic leading-snug text-cream-50/85 md:text-[24px]">
                {term.short_definition}
              </p>
            )}
          </div>
        </section>

        <section className="bg-cream-50 py-16">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 md:px-10 lg:grid-cols-[1fr_280px]">
            <article className="prose-glossary max-w-[680px]">
              {term.long_definition ? (
                <div className="font-sans text-[17px] leading-[1.75] text-charcoal-900 [&_p]:mb-5 [&_p]:first:first-letter:float-left [&_p]:first:first-letter:mr-3 [&_p]:first:first-letter:font-display [&_p]:first:first-letter:text-[64px] [&_p]:first:first-letter:leading-[0.85] [&_p]:first:first-letter:text-bronze-600">
                  <ReactMarkdown>{term.long_definition}</ReactMarkdown>
                </div>
              ) : (
                <p className="font-sans text-[16px] text-charcoal-500">
                  Full definition coming soon.
                </p>
              )}

              <div className="mt-12 border-t border-bronze-600/30 pt-10">
                <Eyebrow>Have a question about this term?</Eyebrow>
                <h3 className="mt-4 font-display text-[24px] text-navy-900">
                  Ask Currency Thoughts.
                </h3>
                <p className="mt-2 font-sans text-[15px] text-charcoal-500">
                  We answer one family money question every week.
                </p>
                <PrimaryButton className="mt-5" onClick={() => setAskOpen(true)}>
                  Submit a Question
                </PrimaryButton>
              </div>
            </article>

            <aside className="space-y-8 lg:sticky lg:top-28 lg:self-start">
              {related.length > 0 && (
                <div>
                  <Eyebrow>Related Terms</Eyebrow>
                  <ul className="mt-4 space-y-2">
                    {related.map((r) => (
                      <li key={r.id}>
                        <Link
                          to="/academy/glossary/$slug"
                          params={{ slug: r.slug }}
                          className="font-sans text-[14px] text-navy-800 hover:text-bronze-600"
                        >
                          {r.term} →
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {lessons.length > 0 && (
                <div>
                  <Eyebrow>Learn More</Eyebrow>
                  <ul className="mt-4 space-y-3">
                    {lessons.map((l) => (
                      <li key={l.id} className="font-sans text-[14px] text-navy-800">
                        {l.title}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="border-l-2 border-bronze-600/60 pl-4">
                <p className="font-sans text-[12px] uppercase tracking-[0.18em] text-bronze-600">
                  Compliance
                </p>
                <p className="mt-2 font-sans text-[12px] leading-relaxed text-charcoal-500">
                  Definitions are educational. They do not constitute legal, tax, or
                  investment advice. Policy terms are governed by the issuing carrier's
                  contract.
                </p>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
      <AskQuestionModal
        open={askOpen}
        onClose={() => setAskOpen(false)}
        contextLabel={`Glossary: ${term.term}`}
      />
    </div>
  );
}
